import React, { useState } from 'react';
import axios from 'axios';
import { IItemsFilterProps } from '../components/shared/filters/ItemsFilter';
import { useRequestPriority } from '../contexts/RequestPriorityContext';
import { useSession } from '../contexts/SessionContext';
import { CERTIFICATIONS_OPTIONS } from '../helpers';
import { formatWithFullPath } from '../helpers/tribe';
import { getComponentRefs, getFilters, getScopeRefs, TagService } from '../services';
import {
  computeEnablers,
  computeInfrastructureComponent,
  computeRemediationResults,
  computeResults,
  computeScopeRules,
  uniq,
} from '../utils';

export const useFilters = ({
  snapshot,
  availableDates,
}: {
  snapshot: string | undefined;
  availableDates: Array<string>;
}): {
  filters: IItemsFilterProps[];
  availableDates: string[];
  loading: boolean;
} => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [filters, setFilters] = useState<Array<IItemsFilterProps>>([]);
  const { profileType } = useSession();
  const { reloadFiltersTrigger, setApplicationsStatus, setTotalStatus, reloadFiltersStatus } = useRequestPriority();

  React.useEffect(() => {
    const request = axios.CancelToken.source();

    if (reloadFiltersStatus === 'shouldCancel') {
      return request.cancel();
    }

    const fetchFilters = async () => {
      setFilters([]);
      setApplicationsStatus('shouldCancel');
      setTotalStatus('shouldCancel');
      if (!snapshot || availableDates.length === 0) return;

      try {
        setLoading(true);
        if (filters.length === 0 || snapshot) {
          const [filters, tags, componentRefs, scopeRefs] = await Promise.all([
            getFilters(snapshot),
            TagService.getTags(request),
            getComponentRefs(request),
            getScopeRefs(request),
          ]);

          const tagsName = tags?.map((tag) => tag.name);
          const computedResults = computeResults(filters.resultType, componentRefs);
          const computedInfrastructureComponent = computeInfrastructureComponent(filters.infrastructureComponent);
          const computedRemediationResults = computeRemediationResults(filters.remediationTarget, componentRefs);
          const computedScopeResults = computeScopeRules(filters.scopeResults, scopeRefs);
          const computedEnablerResults = filters.enablers ? computeEnablers(filters.enablers) : [];

          // Those 2 lignes are mandatory for the filters to be used in the CategoryFilter component
          formatWithFullPath(filters.functionalArchitecture);
          formatWithFullPath(filters.tribe);
          formatWithFullPath(filters.ownershipRc);
          formatWithFullPath(filters.managementRc);

          setFilters([
            {
              label: 'Trigrams',
              options: filters.trigram?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'trigrams',
              nodes: undefined,
              placeholder: 'MPS, UPS...',
            },
            {
              label: 'Tribes',
              options: [],
              nodes: filters.tribe,
              queryParamKey: 'tribes',
              placeholder: 'GBSU/ARC...',
            },
            {
              label: 'Countries',
              options: filters.country?.map((item) => ({ name: item, key: item })),
              nodes: undefined,
              queryParamKey: 'countries',
              placeholder: 'France, Canada...',
            },
            {
              label: 'Applications names',
              options: filters.iAppliName?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'names',
              nodes: undefined,
              placeholder: 'X-ONE, BACARDI...',
            },
            {
              label: 'Criticalities',
              options: filters.criticality?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'criticalities',
              nodes: undefined,
              placeholder: 'Low, Medium...',
            },
            {
              label: 'Managed by',
              options: filters.managedBy?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'managedBy',
              nodes: undefined,
              placeholder: 'GBIS-GBSU...',
            },
            {
              label: 'Application portfolio',
              options: filters.portfolio?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'portfolio',
              nodes: undefined,
              placeholder: 'GBIS-GBTO...',
            },
            {
              label: 'Application type',
              options: filters.applicationType?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'applicationTypes',
              placeholder: 'In house...',
            },
            {
              label: 'Tags',
              options: tagsName?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'tags',
              placeholder: 'Forex...',
            },
            {
              label: 'Status',
              options: filters.status?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'status',
              placeholder: 'In Production...',
            },
            {
              label: 'Architecture type',
              options: filters.architectureType?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'architectureTypes',
              nodes: undefined,
              placeholder: 'Service...',
            },
            {
              label: 'Functional architecture',
              options: [],
              queryParamKey: 'functionalArchitecture',
              nodes: filters.functionalArchitecture,
              placeholder: 'Deal processing...',
            },
            {
              label: 'Is amer managed',
              options: filters.amerManaged?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'isAmerManaged',
              nodes: undefined,
              placeholder: 'True',
            },
            {
              label: 'Certification status',
              options: CERTIFICATIONS_OPTIONS,
              nodes: undefined,
              queryParamKey: 'componentStatus',
              placeholder: 'Certified...',
            },
            {
              label: 'Asset zone',
              options: filters.assetZones?.filter((item) => item).map((item) => ({ name: item, key: item })),
              nodes: undefined,
              queryParamKey: 'assetZones',
              placeholder: 'EUR, ASIA...',
            },
            {
              label: 'Languages',
              options: filters.languages
                ? uniq(filters.languages)?.map((item) => ({ name: item, key: item }))
                : undefined,
              nodes: undefined,
              queryParamKey: 'languages',
              placeholder: 'Java, Python...',
            },
            {
              label: 'Components usage',
              options: [],
              nodes: computedResults,
              queryParamKey: 'presenceResults',
              placeholder: 'SG Connect, Python...',
              id: 'result-category-picker',
            },
            {
              label: 'Infrastructure component',
              options: [],
              nodes: computedInfrastructureComponent,
              queryParamKey: 'infrastructureComponent',
              placeholder: 'Windows, Oracle...',
            },
            {
              label: 'Nova compatibility',
              options: filters.novaCompatible?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'novaCompatibility',
              placeholder: 'Yes, No...',
              nodes: undefined,
            },
            {
              label: 'Ownership RC',
              options: [],
              nodes: filters.ownershipRc,
              queryParamKey: 'ownershipRc',
              placeholder: 'MARK/FIC...',
            },
            {
              label: 'Owned by',
              options: filters.ownedBy?.map((item) => ({ name: item, key: item })),
              queryParamKey: 'ownedBy',
              nodes: undefined,
              placeholder: 'GBIS...',
            },
            {
              label: 'Scope results',
              options: [],
              queryParamKey: 'scopeResults',
              nodes: computedScopeResults,
              placeholder: 'Security...',
              propagation: true,
            },
            {
              label: 'Management RC',
              options: [],
              nodes: filters.managementRc,
              queryParamKey: 'managementRc',
              placeholder: 'GBSU/ARC...',
            },
            {
              label: 'Target',
              options: [],
              nodes: computedRemediationResults,
              queryParamKey: 'remediationTarget',
              placeholder: 'Not processed...',
            },
            {
              label: 'IAM',
              options: filters.iamFilters?.filter((item) => !!item).map((item) => ({ name: item, key: item })),
              nodes: undefined,
              queryParamKey: 'iam',
              placeholder: 'OSMA',
            },
            {
              label: 'Enablers',
              options: [],
              nodes: computedEnablerResults,
              queryParamKey: 'enablers',
              placeholder: 'Network...',
            },
            {
              label: 'Application regulation',
              options: filters.applicationRegulation
                ?.filter((item) => !!item)
                .map((item) => ({ name: item, key: item })),
              nodes: undefined,
              queryParamKey: 'applicationRegulations',
              placeholder: 'CAC...',
            },
          ]);
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.debug('Request was cancelled');
        } else {
          console.error(error);
        }
      } finally {
        setLoading(false);
        if (profileType !== undefined) {
          setApplicationsStatus('shouldLoad');
        }
      }
    };

    if (reloadFiltersStatus === 'shouldLoad') {
      fetchFilters();
    }

    return () => request.cancel();

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadFiltersTrigger, snapshot]);

  return { filters, availableDates, loading };
};
